// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $GeoLocation,
  $RegattaTracker,
  $TrackerPosition,
} from 'common.types';
import type {
  $FullRegattaCourseRace,
  $Model,
} from 'common.data';

// Global Enums
import {
  RegattaCoursePointType,
} from 'common.data';
import {
  webPages,
  webSections,
} from 'common.enums';

// Global Components
import * as React from 'react';
import {
  InfoBox,
  Marker,
  Polyline,
} from '@react-google-maps/api';
import {
  FullScreen,
} from '@chiragrupani/fullscreen-react';

// Helpers
import _ from 'lodash';
import moment from 'moment';
import ErrorLog from '@vroskus/library-sentry/dist/browser';
import {
  getMapGeoCenterPosition,
  getMapZoom,
  shortenText,
} from 'common.helpers/dist/browser';

// Services
import Services from '../../../../services';

// Components
import Section from '../../../layout/Section';
import Loader from '../../../layout/Loader';
import Map from '../../../common/Map';
import InputSlider from '../../../form/InputSlider';

// Types
type $Tracker = {
  id: string;
  path: Array<$TrackerPosition>;
};

type $Props = $Translate & {
  item: $Model<'Regatta'>;
};

type $State = {
  activeRegattaCourseRace: $FullRegattaCourseRace | null;
  center: $GeoLocation;
  data: Array<$FullRegattaCourseRace>;
  dataRegattaTrackers: Array<$RegattaTracker>;
  dataTrackerPositions: Array<$TrackerPosition>;
  detailTrackerIds: Array<number>;
  fullScreen: boolean;
  loading: boolean;
  play: null | ReturnType<typeof setTimeout>;
  regattaCoursePointTrackerIds: Array<number>;
  showTrackerDetailsId: null | number;
  sliderValue: number;
  trackersToShow: Array<$Tracker>;
  zoom: number;
};

const getSliderTimeData = ({
  end,
  max,
  start,
  value,
}: {
  end: Date;
  max: number;
  start: Date;
  value: number;
}): {
  date: Date;
  duration: number;
} => {
  const startTimestamp: number = moment(start).unix();
  const endTimestamp: number = moment(end).unix();
  const diffTime: number = endTimestamp - startTimestamp;
  const duration: number = (diffTime / max) * value;

  return {
    date: moment.unix(startTimestamp + duration).toDate(),
    duration,
  };
};

const getColor = (value: number): string => {
  const hue: number = value * 137.508;

  return `hsl(${hue},80%,35%)`;
};

const getPixelOffset = (): google.maps.Size => {
  try {
    return new window.google.maps.Size(
      7,
      -10,
    );
  } catch (err) {
    const error = err as Error;

    ErrorLog.exception(error);

    return undefined;
  }
};

const maxSliderValue: number = 1000;

class TrackerSectionView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      activeRegattaCourseRace: null,
      center: {
        lat: 0,
        lng: 0,
      },
      data: [],
      dataRegattaTrackers: [],
      dataTrackerPositions: [],
      detailTrackerIds: [],
      fullScreen: false,
      loading: false,
      play: null,
      regattaCoursePointTrackerIds: [],
      showTrackerDetailsId: null,
      sliderValue: maxSliderValue,
      trackersToShow: [],
      zoom: 15,
    };
  }

  componentDidMount(): void {
    this.setupSocket();
    this.init();
  }

  async onSliderMove(newValue: number): Promise<void> {
    const {
      activeRegattaCourseRace,
    } = this.state;

    await this.setStateAsync({
      sliderValue: newValue,
    });

    if (activeRegattaCourseRace !== null
      && activeRegattaCourseRace.Ongoing
      && newValue === maxSliderValue
    ) {
      await this.startPlay();
    }

    await this.setTrackerPositionsToShow();
  }

  async getData(): Promise<void> {
    const {
      item,
    } = this.props;

    try {
      await this.setStateAsync({
        loading: true,
      });

      const {
        RegattaCourseRaces,
        RegattaTrackers,
      } = await Services.Api.getRegattaTrackerSectionData({
        RegattaId: item.id,
      });

      await this.setStateAsync({
        data: RegattaCourseRaces,
        dataRegattaTrackers: RegattaTrackers,
        loading: false,
      });
    } catch (error) {
      await this.setStateAsync({
        loading: false,
      });

      ErrorLog.exception(error as Error);
    }
  }

  async getTrackerPositions(): Promise<void> {
    const {
      activeRegattaCourseRace,
      dataRegattaTrackers,
      regattaCoursePointTrackerIds,
    } = this.state;

    try {
      await this.setStateAsync({
        loading: true,
      });

      let dataTrackerPositions = [];

      if (activeRegattaCourseRace !== null) {
        const params = {
          from: activeRegattaCourseRace.StartDate.toString(),
          ids: [
            ...dataRegattaTrackers.map((regattaTracker: $RegattaTracker) => regattaTracker.id),
            ...regattaCoursePointTrackerIds,
          ].join(','),
          to: activeRegattaCourseRace.EndDate.toString(),
        };

        dataTrackerPositions = await Services.Tracker.getPositions(params);
      }

      await this.setStateAsync({
        dataTrackerPositions,
        loading: false,
      });
    } catch (error) {
      await this.setStateAsync({
        loading: false,
      });

      ErrorLog.exception(error as Error);
    }
  }

  async setTrackerPositionsToShow(): Promise<void> {
    const {
      activeRegattaCourseRace,
      dataTrackerPositions,
      regattaCoursePointTrackerIds,
      sliderValue,
    } = this.state;

    const trackersToShow: Array<$Tracker> = [];
    let sliderTime: Date = new Date();

    if (activeRegattaCourseRace !== null) {
      if (sliderValue === maxSliderValue) {
        if (activeRegattaCourseRace.Ongoing === false) {
          sliderTime = moment(activeRegattaCourseRace.EndDate).toDate();
        }
      } else {
        const dateParams = {
          end: activeRegattaCourseRace.EndDate,
          max: maxSliderValue,
          start: activeRegattaCourseRace.StartDate,
          value: sliderValue,
        };

        const {
          date,
        } = getSliderTimeData(dateParams);

        sliderTime = date;
      }
    }

    const fromTime = moment(sliderTime)
      .subtract(
        1,
        'minute',
      )
      .toDate();

    const filteredTrackerPositions = _.filter(
      dataTrackerPositions,
      (trackerPosition: $TrackerPosition) => {
        const trackerPositionDate: Date = moment(trackerPosition.time).toDate();

        return trackerPositionDate > fromTime && trackerPositionDate <= sliderTime;
      },
    );

    const groupedTrackerPositions = _.groupBy(
      filteredTrackerPositions,
      'id',
    );

    _.map(
      groupedTrackerPositions,
      (path: Array<$TrackerPosition>, id: string) => {
        const trackerId: number = Number(id);

        if (regattaCoursePointTrackerIds.includes(trackerId) === false) {
          const tracker: $Tracker = {
            id,
            path,
          };

          trackersToShow.push(tracker);
        }

        return null;
      },
    );

    await this.setStateAsync({
      trackersToShow,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  setupSocket(): void {
    const {
      item,
    } = this.props;

    Services.Socket.on({
      action: (data) => {
        if (data.RegattaId === item.id && data.modelName === 'RegattaCourseRace') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  async setActiveRegattaCourseRace(
    activeRegattaCourseRace: $FullRegattaCourseRace,
  ): Promise<void> {
    const {
      center,
      zoom,
    } = this.state;

    await this.stopPlay();

    const regattaCoursePointTrackerIds: Array<number> = [];
    let sliderValue: number = 10;
    let updatedCenter = center;
    let updatedZoom = zoom;

    if (activeRegattaCourseRace.RegattaCourse.RegattaCoursePoints.length > 0) {
      updatedCenter = getMapGeoCenterPosition({
        data: activeRegattaCourseRace.RegattaCourse.RegattaCoursePoints,
      });
      updatedZoom = getMapZoom({
        data: activeRegattaCourseRace.RegattaCourse.RegattaCoursePoints,
      });
    }

    activeRegattaCourseRace.RegattaCourse.RegattaCoursePoints.forEach(({
      TrackerId,
      TrackerId2,
    }) => {
      if (TrackerId) {
        regattaCoursePointTrackerIds.push(TrackerId);
      }

      if (TrackerId2) {
        regattaCoursePointTrackerIds.push(TrackerId2);
      }
    });

    if (activeRegattaCourseRace.Ongoing) {
      sliderValue = maxSliderValue;

      await this.startPlay();
    }

    await this.setStateAsync({
      activeRegattaCourseRace,
      center: updatedCenter,
      regattaCoursePointTrackerIds,
      sliderValue,
      zoom: updatedZoom,
    });

    await this.getTrackerPositions();

    await this.setTrackerPositionsToShow();
  }

  async appendTrackerPositions(): Promise<void> {
    const {
      activeRegattaCourseRace,
      dataTrackerPositions,
    } = this.state;

    try {
      if (activeRegattaCourseRace !== null) {
        const latestPosition: $TrackerPosition | undefined = _.last(dataTrackerPositions);

        const params = {
          from: latestPosition ? latestPosition.time.toString() : undefined,
          ids: '1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,24,25',
          to: (new Date()).toString(),
        };

        const latestTrackerPositions = await Services.Tracker.getPositions(params);

        await this.setStateAsync({
          dataTrackerPositions: [
            ...dataTrackerPositions,
            ...latestTrackerPositions,
          ],
        });
      }
    } catch (err) {
      const error = err as Error;

      ErrorLog.exception(error);
    }
  }

  async init(): Promise<void> {
    await this.getData();

    const {
      data,
    } = this.state;

    if (data.length > 0) {
      const latestRegattaCourseRace = data[data.length - 1];

      await this.setActiveRegattaCourseRace(latestRegattaCourseRace);
    }
  }

  async startPlay() {
    const {
      activeRegattaCourseRace: initialActiveRegattaCourseRace,
    } = this.state;

    const play = setInterval(
      async () => {
        const {
          activeRegattaCourseRace,
          sliderValue,
        } = this.state;

        if (activeRegattaCourseRace !== null) {
          if (activeRegattaCourseRace.Ongoing === true) {
            await this.appendTrackerPositions();
          } else if (sliderValue !== maxSliderValue) {
            const duration: number = moment.duration(
              moment(activeRegattaCourseRace.EndDate)
                .diff(moment(activeRegattaCourseRace.StartDate)),
            ).asSeconds();
            const step: number = maxSliderValue / duration;

            await this.setStateAsync({
              sliderValue: sliderValue + step * activeRegattaCourseRace.UpadateInterval,
            });
          }
        }

        await this.setTrackerPositionsToShow();
      },
      (initialActiveRegattaCourseRace !== null && initialActiveRegattaCourseRace.Ongoing === true)
        ? initialActiveRegattaCourseRace.UpadateInterval * 1000
        : 1000,
    );

    await this.setStateAsync({
      play,
    });
  }

  async stopPlay() {
    const {
      play,
    } = this.state;

    if (play !== null) {
      clearInterval(play);

      await this.setStateAsync({
        play: null,
      });
    }
  }

  /* eslint-disable-next-line class-methods-use-this */
  renderMapRegattaCoursePoint({
    color,
    id,
    label,
    position,
  }: {
    color: string;
    id: string;
    label?: null | string;
    position: $GeoLocation;
  }) {
    return (
      <React.Fragment key={id}>
        <Marker
          icon={{
            fillColor: color,
            fillOpacity: 0.35,
            path: 'M -5, 0a 5,5 0 1,1 10,0a 5,5 0 1,1 -10,0',
            strokeColor: color,
            strokeOpacity: 0.8,
            strokeWeight: 2,
          }}
          key={id}
          position={position}
        />
        {label && (
          <Marker
            icon={{
              anchor: {
                equals: () => false,
                x: 0,
                y: 15,
              },
              path: 'M 0 0 z',
            }}
            label={{
              color: 'gray',
              fontSize: '11px',
              text: label,
            }}
            position={position}
          />
        )}
      </React.Fragment>
    );
  }

  /* eslint-disable-next-line class-methods-use-this */
  renderMapRegattaCourseLine({
    id,
    position1,
    position2,
  }: {
    id: string;
    position1: $GeoLocation;
    position2: $GeoLocation;
  }) {
    return (
      <Polyline
        key={id}
        options={{
          icons: [
            {
              icon: {
                path: 'M 0,-1 0,1',
                scale: 4,
                strokeOpacity: 0.5,
                strokeWeight: 0.5,
              },
              offset: '0',
              repeat: '20px',
            },
          ],
          strokeColor: 'gray',
          strokeWeight: 0,
        }}
        path={[position1, position2]}
      />
    );
  }

  renderMapRegattaCoursePoints() {
    const {
      activeRegattaCourseRace,
    } = this.state;

    return (activeRegattaCourseRace !== null ? (
      /* eslint-disable-next-line complexity */
      activeRegattaCourseRace.RegattaCourse.RegattaCoursePoints.map((
        dataItem: $Model<'RegattaCoursePoint'>,
      ) => {
        let position1: $GeoLocation = {
          lat: dataItem.Lat,
          lng: dataItem.Lng,
        };

        if (dataItem.TrackerId) {
          const {
            dataTrackerPositions,
          } = this.state;

          const trackerPosition1 = _.findLast(
            dataTrackerPositions,
            {
              id: dataItem.TrackerId,
            },
          );

          if (trackerPosition1) {
            position1 = trackerPosition1;
          }
        }

        const point1Params = {
          color: dataItem.Color,
          id: `${dataItem.id}1`,
          label: dataItem.Name,
          position: position1,
        };

        const view = [this.renderMapRegattaCoursePoint(point1Params)];

        if (dataItem.Type === RegattaCoursePointType.gate
          && dataItem.Lat2
          && dataItem.Lng2
          && dataItem.Color2
        ) {
          let position2: $GeoLocation = {
            lat: dataItem.Lat2,
            lng: dataItem.Lng2,
          };

          if (dataItem.TrackerId2) {
            const {
              dataTrackerPositions,
            } = this.state;

            const trackerPosition2 = _.findLast(
              dataTrackerPositions,
              {
                id: dataItem.TrackerId2,
              },
            );

            if (trackerPosition2) {
              position2 = trackerPosition2;
            }
          }

          const point2Params = {
            color: dataItem.Color2,
            id: `${dataItem.id}2`,
            label: dataItem.Name2,
            position: position2,
          };

          view.push(this.renderMapRegattaCoursePoint(point2Params));

          const lineParams = {
            id: `${dataItem.id}l`,
            position1,
            position2,
          };

          view.push(this.renderMapRegattaCourseLine(lineParams));
        }

        return view;
      })) : null);
  }

  renderMapTrackerPosition({
    id,
    path,
  }: {
    id: string;
    path: Array<$TrackerPosition>;
  }) {
    const {
      dataRegattaTrackers,
      detailTrackerIds,
    } = this.state;

    const trackerId: number = Number(id);

    const regattaTracker: $RegattaTracker | undefined = _.find(
      dataRegattaTrackers,
      {
        id: trackerId,
      },
    );

    const position: $TrackerPosition | undefined = _.last(path);

    let text1: string = String(id);
    let text2: string | undefined;

    if (regattaTracker && position) {
      text1 = regattaTracker.Name;
      text2 = shortenText(
        regattaTracker.SubName,
        21,
      );

      let lineOpacity: number = 0.45;
      let fillOpacity: number = 0.15;

      if (detailTrackerIds.includes(position.id) === true) {
        lineOpacity = 0.75;
        fillOpacity = 0.75;
      }

      const color: string = getColor(
        trackerId,
      );

      return (
        <React.Fragment key={id}>
          <Marker
            icon={{
              fillColor: color,
              fillOpacity,
              path: 'M 0,6 -2,6 -3,2 -2,-2 -1,-4 0,-6 1,-4 2,-2 3,2 2,6 z',
              rotation: position.cog,
              strokeColor: color,
              strokeOpacity: lineOpacity,
              strokeWeight: 1.2,
            }}
            key={id}
            onClick={() => {
              let updatedDetailTrackerIds = detailTrackerIds;

              if (detailTrackerIds.includes(trackerId) === true) {
                updatedDetailTrackerIds = detailTrackerIds.filter((i) => i !== trackerId);
              } else {
                updatedDetailTrackerIds.unshift(trackerId);
              }

              this.setState({
                detailTrackerIds: updatedDetailTrackerIds,
              });
            }}
            position={position}
          >
            <InfoBox
              options={{
                closeBoxURL: '',
                pixelOffset: getPixelOffset(),
              }}
              position={position as unknown as google.maps.LatLng}
            >
              <div className={'TrackerSection-TrackerLabel whitespace-nowrap'}>
                <div
                  style={{
                    color,
                    opacity: lineOpacity,
                  }}
                >
                  {text2 || text1}
                </div>
              </div>
            </InfoBox>
          </Marker>
          <Polyline
            options={{
              strokeColor: color,
              strokeOpacity: 0.3,
              strokeWeight: 0.3,
            }}
            path={path}
          />
        </React.Fragment>
      );
    }

    return null;
  }

  renderMapTrackerPositions() {
    const {
      trackersToShow,
    } = this.state;

    return trackersToShow.map((tracker: $Tracker) => this.renderMapTrackerPosition({
      id: tracker.id,
      path: tracker.path,
    }));
  }

  renderMap() {
    const {
      center,
      fullScreen,
      zoom,
    } = this.state;

    return (
      <Map
        center={center}
        height={fullScreen === true ? '90vh' : 400}
        zoom={zoom}
      >
        {this.renderMapRegattaCoursePoints()}
        {this.renderMapTrackerPositions()}
      </Map>
    );
  }

  renderTrackerDetails(trackerId: number) {
    const {
      dataRegattaTrackers,
      detailTrackerIds,
      trackersToShow,
    } = this.state;

    const regattaTracker: $RegattaTracker | undefined = _.find(
      dataRegattaTrackers,
      {
        id: trackerId,
      },
    );

    const tracker = _.find(
      trackersToShow,
      {
        id: String(trackerId),
      },
    ) as $Tracker | undefined;

    const position: $TrackerPosition | undefined = _.last(tracker?.path || []);

    let text1: string = String(trackerId);
    let text2: string | undefined;

    const color: string = getColor(
      trackerId,
    );

    if (regattaTracker) {
      text1 = regattaTracker.Name;
      text2 = shortenText(
        regattaTracker.SubName,
        21,
      );

      return (
        <div
          className={'relative rounded-lg bg-white py-2 pr-5 mb-2'}
          key={trackerId}
        >
          <button
            className={'absolute close la la-times right-1 top-1'}
            onClick={() => this.setState({
              detailTrackerIds: detailTrackerIds.filter((i) => i !== trackerId),
            })}
            type={'button'}
          />
          <button
            className={'flex flex-row items-center'}
            onClick={() => {
              const center: $GeoLocation = {
                lat: position.lat,
                lng: position.lng,
              };

              if (position) {
                this.setState({
                  center,
                });
              }
            }}
            type={'button'}
          >
            <svg
              height={'32'}
              viewBox={'-8 -8 16 16'}
              width={'32'}
              xmlns={'http://www.w3.org/2000/svg'}
            >
              <g fill={color}>
                <path d={'M 0,6 -2,6 -3,2 -2,-2 -1,-4 0,-6 1,-4 2,-2 3,2 2,6 z'} />
              </g>
            </svg>
            <div className={'grow text-left pr-3'}>
              <div className={'leading-2'}>{text1}</div>
              <div
                className={'leading-3'}
                style={{
                  color,
                }}
              >
                {text2}
              </div>
            </div>
            {position && (
              <div className={'text-gray-600 leading-3 text-right min-w-12'}>
                <div className={'my-1'}>
                  {`${position.sog}kts`}
                </div>
                <div>
                  {`${position.cog}°`}
                </div>
              </div>
            )}
          </button>
        </div>
      );
    }

    return null;
  }

  renderTrackersDetails() {
    const {
      detailTrackerIds,
    } = this.state;

    return (
      <div className={'TrackerSection-TrackersDetails absolute left-3 bottom-3 rounded-lg bg-white-tr'}>
        {detailTrackerIds.map((trackerId: number) => this.renderTrackerDetails(trackerId))}
      </div>
    );
  }

  renderControlsCourseSelect() {
    const {
      activeRegattaCourseRace,
      data,
    } = this.state;

    return (activeRegattaCourseRace !== null ? (
      <div className={'custom-select w-full sm:max-w-36'}>
        <select
          className={'form-control rounded-full relative btn_primary btn_outlined uppercase py-2 text-gray-600 h-full w-full'}
          onChange={(e) => {
            const {
              value,
            } = e.target;

            const selectedRegattaCourseRace: $FullRegattaCourseRace | undefined = _.find(
              data,
              {
                id: value,
              },
            );

            if (selectedRegattaCourseRace) {
              this.setActiveRegattaCourseRace(selectedRegattaCourseRace);
            }
          }}
          value={activeRegattaCourseRace.id}
        >
          {data.map((dataItem: $FullRegattaCourseRace) => (
            <option
              key={dataItem.id}
              value={dataItem.id}
            >
              {dataItem.Name}
            </option>
          ))}
        </select>
      </div>
    ) : null);
  }

  renderControlsTimestamp() {
    const {
      activeRegattaCourseRace,
      sliderValue,
    } = this.state;

    let dateValue: string = '-';
    let durationValue: string = '-';

    if (activeRegattaCourseRace !== null) {
      const dateParams = {
        end: activeRegattaCourseRace.EndDate,
        max: maxSliderValue,
        start: activeRegattaCourseRace.StartDate,
        value: sliderValue,
      };

      const {
        date,
        duration,
      } = getSliderTimeData(dateParams);

      dateValue = moment(date).format('HH:mm:ss');

      durationValue = moment.utc(duration * 1000).format(duration > 3600 ? 'h:mm:ss' : 'mm:ss');
    }

    return (
      <div className={'form-control rounded-full rounded-full btn_primary btn_outlined uppercase px-2 py-auto h-10 w-20 text-center leading-3 mr-3'}>
        <div className={'text-gray-600'}>
          {durationValue}
        </div>
        <div className={'text-gray-400 text-xs'}>
          {dateValue}
        </div>
      </div>
    );
  }

  renderControlsFullScreen() {
    const {
      fullScreen,
    } = this.state;

    let className = 'btn_outlined';
    let iconClassName = 'la-expand-arrows-alt';

    if (fullScreen === true) {
      className = 'btn_active';
      iconClassName = 'la-compress-arrows-alt';
    }

    return (
      <button
        className={`${className} btn btn_primary uppercase px-2 py-1 h-10 mr-3`}
        onClick={() => this.setState({
          fullScreen: !fullScreen,
        })}
        type={'button'}
      >
        <em className={`la ${iconClassName} text-2xl`} />
      </button>
    );
  }

  renderControlsPlay() {
    const {
      play,
    } = this.state;

    const className: string = play !== null ? 'btn_active' : 'btn_outlined';

    return (
      <button
        className={`${className} btn btn_primary uppercase px-2 py-1 h-10`}
        onClick={() => {
          if (play !== null) {
            this.stopPlay();
          } else {
            this.startPlay();
          }
        }}
        type={'button'}
      >
        <em className={'la la-play text-2xl'} />
      </button>
    );
  }

  renderControlsSlider() {
    const {
      activeRegattaCourseRace,
      sliderValue,
    } = this.state;

    if (activeRegattaCourseRace !== null) {
      let thumbClassName: string = 'btn_primary btn_outlined';
      let iconClassName: string = 'la-bullseye';

      if (sliderValue === maxSliderValue && activeRegattaCourseRace.Ongoing === true) {
        thumbClassName = 'btn_primary btn_active btn_danger';
        iconClassName = 'la-rss';
      }

      return (
        <InputSlider
          iconClassName={iconClassName}
          max={maxSliderValue}
          name={'track-slider'}
          onChange={(newValue: number) => this.onSliderMove(newValue)}
          thumbClassName={thumbClassName}
          value={sliderValue}
        />
      );
    }

    return null;
  }

  renderControls() {
    const {
      activeRegattaCourseRace,
    } = this.state;

    return (activeRegattaCourseRace !== null ? (
      <div className={'sm:flex'}>
        <div className={'flex mr-0 sm:mr-3 mb-3 sm:mb-0'}>
          <div className={'w-full mr-3'}>
            {this.renderControlsCourseSelect()}
          </div>
          <div>
            {this.renderControlsTimestamp()}
          </div>
          <div>
            {this.renderControlsFullScreen()}
          </div>
          <div>
            {this.renderControlsPlay()}
          </div>
        </div>
        <div className={'flex-1'}>
          {this.renderControlsSlider()}
        </div>
      </div>
    ) : null);
  }

  render() {
    const {
      t,
    } = this.props;
    const {
      data,
      fullScreen,
      loading,
    } = this.state;

    return (data.length > 0 ? (
      <Section
        id={webSections[webPages.regattaPage].tracker}
        title={t('Tracker')}
      >
        <FullScreen
          isFullScreen={fullScreen}
          onChange={(isFullScreen) => this.setState({
            fullScreen: isFullScreen,
          })}
        >
          <div className={fullScreen ? 'p-3 h-100 bg-white' : ''}>
            <Loader
              fade
              loading={loading === true}
            >
              <div className={'mb-3 relative'}>
                {this.renderMap()}
                {this.renderTrackersDetails()}
              </div>
              <div>
                {this.renderControls()}
              </div>
            </Loader>
          </div>
        </FullScreen>
      </Section>
    ) : null);
  }
}

export default TrackerSectionView;
